import './App.css';
import Dashboard from './components/dashboard/Dashboard'
import { createBrowserRouter, RouterProvider, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import PublicMaintenanceView from './components/maintenances/PublicMaintenanceView';
import PublicQuoteView from './components/maintenances/public/PublicQuoteView';
import MainCustomersView from './components/customers/MainCustomersView';
import MainMaintenanceView from './components/maintenances/MainMaintenanceView';
import MaintenanceProcessView from './components/maintenances/MaintenanceProcessView';
import MainSettingsView from './components/settings/MainSettingsView';
import MainDesktopView from './components/desktop/MainDesktopView';
import MainEmployeeView from './components/employees/MainEmployeeView';
import CalendarPage from './components/calendar/CalendarPage';
import MainPartsView from './components/parts/MainPartsView';
import MainJobsView from './components/jobs/MainJobsView';
import MainServicePackagesView from './components/service_packages/MainServicePackagesView';
import CataloguesListView from './components/catalogues/CataloguesListView';
import CatalogueDetailView from './components/catalogues/CatalogueDetailView';
import PriceDriftView from './components/catalogues/PriceDriftView';
import ServicePackageEditView from './components/service_packages/ServicePackageEditView';
import MainInvoicesView from './components/invoices/MainInvoicesView';
import ReportsPage from './components/reports/ReportsPage';
import ImportsListView from './components/imports/ImportsListView';
import MainVehiclesView from './components/vehicles/MainVehiclesView';
import MainServiceBaysView from './components/service-bays/MainServiceBaysView';
import InvoiceDetailView from './components/invoices/InvoiceDetailView';
import { InventoryListView } from './components/inventory';
import MainVendorsView from './components/vendors/MainVendorsView';
import MainSupplierOrdersView from './components/supplier-orders/MainSupplierOrdersView';
import SupplierOrderBuilderView from './components/supplier-orders/builder/SupplierOrderBuilderView';
import MainAiPhoneView from './components/ai-phone/MainAiPhoneView';
import StorageLocationsPage from './components/tire_hotel/StorageLocationsPage';
import TireHotelCardsPage from './components/tire_hotel/TireHotelCardsPage';
import TireHotelCardDetail from './components/tire_hotel/TireHotelCardDetail';
import SalesPipelinePage from './components/tire_hotel/SalesPipelinePage';
import SeasonalCampaignPage from './components/tire_hotel/SeasonalCampaignPage';
import BookingsPage from './components/booking/BookingsPage';
import CounterSaleView from './components/parts-sales/CounterSaleView';
import PartsSalesDeliveriesView from './components/parts-sales/PartsSalesDeliveriesView';
import LahetteDetailView from './components/parts-sales/LahetteDetailView';
import OnboardingWizard from './components/onboarding/OnboardingWizard';
import WorkTimePage from './components/worktime/WorkTimePage';
import ClockPage from './components/worktime/clock/ClockPage';
import DayReconcileView from './components/worktime/clock/DayReconcileView';
import AdminPage from './components/vadmin/AdminPage';
import InvitationAcceptPage from './components/invitation/InvitationAcceptPage';
import GoogleCalendarCallback from './components/settings/GoogleCalendarCallback';
import ScrollToTop from './components/shared/ScrollToTop';
import SubscriptionGate from './components/shared/SubscriptionGate';
import MfaSetupGate from './components/shared/MfaSetupGate';
import SentryTestPage from './components/debug/SentryTestPage';
import PublicFeedbackView from './components/feedback/public/PublicFeedbackView';
import MainCustomerFeedbackView from './components/customer-feedback/MainCustomerFeedbackView';
import PublicFeedbackUnsubscribePage from './components/feedback/public/PublicFeedbackUnsubscribePage';
import PublicReminderOptOutPage from './components/service_reminders/PublicReminderOptOutPage';
import ServiceRemindersPage from './components/service_reminders/ServiceRemindersPage';
import PurchaseInvoicesPage from './components/purchase-invoices/PurchaseInvoicesPage';
import PurchaseInvoiceDetail from './components/purchase-invoices/PurchaseInvoiceDetail';
import PurchaseInboxView from './components/purchase-invoices/PurchaseInboxView';
import PaymentBatchesPage from './components/purchase-invoices/PaymentBatchesPage';
import PaymentBatchDetail from './components/purchase-invoices/PaymentBatchDetail';
import WebsitePage from './components/website/WebsitePage';
import HelpWidget from './components/shared/HelpWidget';
import SupportView from './components/support/SupportView';
import { ThemeProvider, CssBaseline, Box, CircularProgress, Button, Snackbar, Alert } from '@mui/material';
import { useUserStore } from 'store';
import { can, featureEnabled, permissionsPending, strictFeatureEnabled } from './utils/access';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import theme from './theme';
import useIdleRefresh from './hooks/useIdleRefresh';

/**
 * Route protection by permission key (organizations/permission_registry.py).
 * Redirects to home when the current organization does not grant the key.
 */
function PermissionRoute({ children, perm }) {
  const { currentOrganization } = useUserStore();

  // Permissions not delivered yet (a deploy in progress, see usePermissions): hold, do not bounce.
  if (permissionsPending(currentOrganization)) {
    return null;
  }
  if (!can(currentOrganization, perm)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/** Landing page: the dashboard, or the invoices list for roles that never see work orders. */
function HomeRoute() {
  const { currentOrganization } = useUserStore();

  if (permissionsPending(currentOrganization)) return null;
  if (can(currentOrganization, 'work_orders.view')) return <MainDesktopView />;
  if (can(currentOrganization, 'invoices.view')) return <Navigate to="/invoices" replace />;
  return <Navigate to="/settings" replace />;
}

/**
 * Route protection for staff-only pages (Verstaani admin).
 * Redirects to home if user is not staff/superuser.
 */
function StaffProtectedRoute({ children }) {
  const { user } = useUserStore();

  if (!user?.is_staff && !user?.is_superuser) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/**
 * Route protection component for feature flags.
 * Redirects to home if the required feature is disabled.
 */
function FeatureProtectedRoute({ children, featureKey }) {
  const { currentOrganization } = useUserStore();

  if (!featureEnabled(currentOrganization, featureKey)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/**
 * Route protection for opt-in features: unlike FeatureProtectedRoute, a missing flag means
 * denied rather than allowed. Used where fail-open would show a route the org never enabled.
 */
function StrictFeatureProtectedRoute({ children, featureKey }) {
  const { currentOrganization } = useUserStore();

  // Organization context not resolved yet — render nothing rather than bouncing to "/".
  if (!currentOrganization) {
    return null;
  }
  if (!strictFeatureEnabled(currentOrganization, featureKey)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// List of public paths that don't require authentication
const PUBLIC_PATHS = ['/seuranta', '/tarjous', '/invitation', '/debug', '/auth/google-calendar', '/palaute', '/m/'];

function isPublicPath(pathname) {
  return PUBLIC_PATHS.some(path => pathname.startsWith(path));
}

function ImpersonationBanner() {
  const { user } = useUserStore();
  const { t } = useTranslation();

  if (!user?.is_impersonating) return null;

  const BANNER_HEIGHT = 36;

  return (
    <>
      {/* Spacer to push page content down */}
      <Box sx={{ height: BANNER_HEIGHT }} />
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: BANNER_HEIGHT,
          zIndex: 9999,
          backgroundColor: '#e65100',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 2,
          fontSize: '0.875rem',
        }}
      >
        <span>{t('impersonation.banner', { name: user.name, email: user.email })}</span>
        <Button
          size="small"
          variant="outlined"
          sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
          onClick={() => { window.location.href = '/api/impersonate/release/'; }}
        >
          {t('impersonation.stop')}
        </Button>
      </Box>
    </>
  );
}

/** Shows the backend's reason when a write is refused for a missing permission (see index.js). */
function PermissionDeniedSnackbar() {
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const onDenied = (event) => setMessage(event.detail);
    window.addEventListener('verstaani:permission-denied', onDenied);
    return () => window.removeEventListener('verstaani:permission-denied', onDenied);
  }, []);

  return (
    <Snackbar
      open={Boolean(message)}
      autoHideDuration={6000}
      onClose={() => setMessage(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      data-testid="permission-denied-snackbar"
    >
      <Alert severity="warning" onClose={() => setMessage(null)} sx={{ width: '100%' }}>
        {message}
      </Alert>
    </Snackbar>
  );
}

function AppContent() {
  const { user, loading, fetchUser } = useUserStore();
  const location = useLocation();
  const [authChecked, setAuthChecked] = useState(false);

  // Auto-refresh after 2 hours of inactivity to pick up deployments
  useIdleRefresh();

  useEffect(() => {
    const checkAuth = async () => {
      const maxRetries = 3;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          await fetchUser();
          setAuthChecked(true);
          return;
        } catch (error) {
          if (error.message === 'UNAUTHENTICATED') {
            setAuthChecked(true);
            // Only redirect to login if we're not on a public path
            if (!isPublicPath(location.pathname)) {
              const nextPath = encodeURIComponent(window.location.pathname + window.location.search);
              window.location.href = `/accounts/login/?next=${nextPath}`;
            }
            return;
          }
          // Network error - retry after delay (unless last attempt)
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          } else {
            setAuthChecked(true);
          }
        }
      }
    };

    checkAuth();
  }, [fetchUser, location.pathname]);

  // Show loading spinner while checking authentication or fetching user
  if (!authChecked || (loading && !user)) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If on public path, render regardless of authentication status
  const isPublic = isPublicPath(location.pathname);

  // An organization requires two-factor authentication and the user has none: nothing but the
  // setup screen until it is done (the API refuses everything else meanwhile).
  const needsMfaSetup = user && user.mfa_setup_required;

  // If user is logged in but needs onboarding, show the wizard
  const needsOnboarding = user && user.needs_onboarding;

  return (
    <>
      <ScrollToTop />
      <ImpersonationBanner />
      <PermissionDeniedSnackbar />
      <Routes>
        {/* Public routes - always accessible */}
        <Route path="/seuranta/:token" element={<PublicMaintenanceView />} />
        <Route path="/tarjous/:token" element={<PublicQuoteView />} />
        <Route path="/palaute/unsubscribe/:token" element={<PublicFeedbackUnsubscribePage />} />
        <Route path="/palaute/:token" element={<PublicFeedbackView />} />
        {/* Short opt-out path — counted into billed SMS segments, keep it short */}
        <Route path="/m/:token" element={<PublicReminderOptOutPage />} />
        <Route path="/invitation/:token" element={<InvitationAcceptPage />} />
        <Route path="/debug/sentry" element={<SentryTestPage />} />
        <Route path="/auth/google-calendar/callback" element={<GoogleCalendarCallback />} />

        {/* Protected routes - require authentication */}
        {user && (
          <Route
            path="/*"
            element={
              needsMfaSetup ? (
                <MfaSetupGate />
              ) : needsOnboarding ? (
                <OnboardingWizard />
              ) : (
                <>
                  <SubscriptionGate>
                    <Dashboard />
                  </SubscriptionGate>
                  <HelpWidget />
                </>
              )
            }
          >
            {!needsOnboarding && !needsMfaSetup && (
              <>
                {/* Work order routes: every role that sees work orders (scope is applied inside) */}
                <Route path="calendar" element={
                  <PermissionRoute perm="work_orders.view">
                    <CalendarPage />
                  </PermissionRoute>
                } />
                <Route path="maintenances" element={
                  <PermissionRoute perm="work_orders.view">
                    <MainMaintenanceView />
                  </PermissionRoute>
                } />
                {/*
                  Quotes get their own list only when the org opts in. Rows still open
                  /maintenances/:id — a quote's detail page is the work order page.
                */}
                <Route path="quotes" element={
                  <PermissionRoute perm="work_orders.view">
                    <StrictFeatureProtectedRoute featureKey="separate_quotes_list_enabled">
                      <MainMaintenanceView variant="quotes" />
                    </StrictFeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="maintenances/:id" element={
                  <PermissionRoute perm="work_orders.view">
                    <MaintenanceProcessView />
                  </PermissionRoute>
                } />
                {/* Own work days and the time clock belong to calendar.manage (own rows) */}
                <Route path="work-time" element={
                  <PermissionRoute perm="calendar.manage">
                    <FeatureProtectedRoute featureKey="work_hours_reporting_enabled">
                      <WorkTimePage />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="time-clock" element={
                  <PermissionRoute perm="calendar.manage">
                    <FeatureProtectedRoute featureKey="time_clock_enabled">
                      <ClockPage />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="time-clock/confirm" element={
                  <PermissionRoute perm="calendar.manage">
                    <FeatureProtectedRoute featureKey="time_clock_enabled">
                      <DayReconcileView />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="settings" element={<MainSettingsView />} />
                <Route path="vadmin/*" element={
                  <StaffProtectedRoute>
                    <AdminPage />
                  </StaffProtectedRoute>
                } />
                <Route path="" element={<HomeRoute />} />

                {/* Customers, parts, jobs stay open to every member: work order screens use them */}
                <Route path="customers" element={<MainCustomersView />} />
                {/* Tuki ja ohjeet: avustaja, guides and customer service for every member */}
                <Route path="support" element={<SupportView />} />
                <Route path="customer-feedback" element={
                  <PermissionRoute perm="menu.customer_feedback">
                    <MainCustomerFeedbackView />
                  </PermissionRoute>
                } />
                <Route path="service-reminders" element={
                  <PermissionRoute perm="menu.service_reminders">
                    <StrictFeatureProtectedRoute featureKey="service_reminders_available">
                      <ServiceRemindersPage />
                    </StrictFeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="parts" element={<MainPartsView />} />
                <Route path="jobs" element={<MainJobsView />} />

                {/* Routes bound to a permission key; menu.* keys mirror the navigation */}
                <Route path="employees" element={
                  <PermissionRoute perm="team.manage">
                    <MainEmployeeView />
                  </PermissionRoute>
                } />
                <Route path="invoices" element={
                  <PermissionRoute perm="invoices.view">
                    <MainInvoicesView />
                  </PermissionRoute>
                } />
                <Route path="invoices/:id" element={
                  <PermissionRoute perm="invoices.view">
                    <InvoiceDetailView />
                  </PermissionRoute>
                } />
                <Route path="supplier-orders" element={
                  <PermissionRoute perm="menu.supplier_orders">
                    <MainSupplierOrdersView />
                  </PermissionRoute>
                } />
                <Route path="supplier-orders/new" element={
                  <PermissionRoute perm="supplier_orders.manage">
                    <SupplierOrderBuilderView />
                  </PermissionRoute>
                } />
                <Route path="ai-phone" element={
                  <PermissionRoute perm="menu.ai_phone">
                    <FeatureProtectedRoute featureKey="ai_phone_enabled">
                      <MainAiPhoneView />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="reports" element={
                  <PermissionRoute perm="reports.view">
                    <ReportsPage />
                  </PermissionRoute>
                } />
                <Route path="vehicles" element={
                  <PermissionRoute perm="menu.vehicles">
                    <MainVehiclesView />
                  </PermissionRoute>
                } />
                <Route path="service-bays" element={
                  <PermissionRoute perm="menu.service_bays">
                    <MainServiceBaysView />
                  </PermissionRoute>
                } />
                <Route path="imports" element={
                  <PermissionRoute perm="imports.manage">
                    <ImportsListView />
                  </PermissionRoute>
                } />
                <Route path="catalogues" element={
                  <PermissionRoute perm="menu.catalogues">
                    <FeatureProtectedRoute featureKey="vendor_catalogues_enabled">
                      <CataloguesListView />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="catalogues/price-drift" element={
                  <PermissionRoute perm="menu.catalogues">
                    <FeatureProtectedRoute featureKey="vendor_catalogues_enabled">
                      <PriceDriftView />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="catalogues/:id" element={
                  <PermissionRoute perm="menu.catalogues">
                    <FeatureProtectedRoute featureKey="vendor_catalogues_enabled">
                      <CatalogueDetailView />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="inventory" element={
                  <PermissionRoute perm="menu.inventory">
                    <FeatureProtectedRoute featureKey="inventory_tracking_enabled">
                      <InventoryListView />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="vendors" element={
                  <PermissionRoute perm="menu.vendors">
                    <FeatureProtectedRoute featureKey="inventory_tracking_enabled">
                      <MainVendorsView />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="tire-hotel" element={
                  <PermissionRoute perm="menu.tire_hotel">
                    <FeatureProtectedRoute featureKey="tire_hotel_enabled">
                      <TireHotelCardsPage />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="tire-hotel/pipeline" element={
                  <PermissionRoute perm="menu.tire_hotel">
                    <FeatureProtectedRoute featureKey="tire_hotel_enabled">
                      <SalesPipelinePage />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="tire-hotel/campaigns" element={
                  <PermissionRoute perm="menu.tire_hotel">
                    <FeatureProtectedRoute featureKey="tire_hotel_enabled">
                      <SeasonalCampaignPage />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="tire-hotel/:id" element={
                  <PermissionRoute perm="menu.tire_hotel">
                    <FeatureProtectedRoute featureKey="tire_hotel_enabled">
                      <TireHotelCardDetail />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="tire-hotel/locations" element={
                  <PermissionRoute perm="menu.tire_hotel">
                    <FeatureProtectedRoute featureKey="tire_hotel_enabled">
                      <StorageLocationsPage />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="bookings" element={
                  <PermissionRoute perm="menu.bookings">
                    <FeatureProtectedRoute featureKey="booking_beta">
                      <BookingsPage />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="nettisivut" element={
                  <PermissionRoute perm="menu.website">
                    <StrictFeatureProtectedRoute featureKey="website_beta">
                      <WebsitePage />
                    </StrictFeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="kassamyynti" element={
                  <PermissionRoute perm="parts_sales.manage">
                    <FeatureProtectedRoute featureKey="counter_sales_available">
                      <CounterSaleView />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="lahetteet" element={
                  <PermissionRoute perm="parts_sales.manage">
                    <FeatureProtectedRoute featureKey="parts_sales_available">
                      <PartsSalesDeliveriesView />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="lahetteet/:id" element={
                  <PermissionRoute perm="parts_sales.manage">
                    <FeatureProtectedRoute featureKey="parts_sales_available">
                      <LahetteDetailView />
                    </FeatureProtectedRoute>
                  </PermissionRoute>
                } />
                {/* Ostoreskontra (purchase ledger): opt-in feature, strict gate. Static paths before :id. */}
                <Route path="ostolaskut" element={
                  <PermissionRoute perm="purchase_invoices.view">
                    <StrictFeatureProtectedRoute featureKey="purchase_ledger_available">
                      <PurchaseInvoicesPage />
                    </StrictFeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="ostolaskut/saapuneet" element={
                  <PermissionRoute perm="purchase_invoices.view">
                    <StrictFeatureProtectedRoute featureKey="purchase_ledger_available">
                      <PurchaseInboxView />
                    </StrictFeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="ostolaskut/maksuun" element={
                  <PermissionRoute perm="purchase_invoices.view">
                    <StrictFeatureProtectedRoute featureKey="purchase_ledger_available">
                      <PaymentBatchesPage />
                    </StrictFeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="ostolaskut/maksuerat/:id" element={
                  <PermissionRoute perm="purchase_invoices.view">
                    <StrictFeatureProtectedRoute featureKey="purchase_ledger_available">
                      <PaymentBatchDetail />
                    </StrictFeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="ostolaskut/:id" element={
                  <PermissionRoute perm="purchase_invoices.view">
                    <StrictFeatureProtectedRoute featureKey="purchase_ledger_available">
                      <PurchaseInvoiceDetail />
                    </StrictFeatureProtectedRoute>
                  </PermissionRoute>
                } />
                <Route path="huoltopaketit" element={
                  <PermissionRoute perm="service_packages.manage">
                    <MainServicePackagesView />
                  </PermissionRoute>
                } />
                <Route path="huoltopaketit/:id" element={
                  <PermissionRoute perm="service_packages.manage">
                    <ServicePackageEditView />
                  </PermissionRoute>
                } />
              </>
            )}
          </Route>
        )}
      </Routes>
    </>
  );
}

function AppShell() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppContent />
    </ThemeProvider>
  );
}

const router = createBrowserRouter([
  { path: '*', Component: AppShell }
]);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
